import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  CompanyListCompanyDto,
  CompanyListDto,
} from 'models/dtoTypes';
import axios from 'axios';
import { get } from 'lodash';
import { date2str } from 'utils/date';
import { styled } from '@mui/material/styles';
import ResourceDetailContainer, { ITab } from 'kosmos/components/ResourceDetailContainer';
import Notice from 'kosmos/components/Notice';
// import Link from 'components/Link';
import {
  GetCompanyList,
  UpdateCompanyList,
} from 'services/companyList';
import CompanyListVerificationTable from './_companyListVerificationTable';
import CompanyListEditTable from './_companyListEditTable';
import generateNonce from 'utils/generateNonce';
import VerificationStatusChip from './_verificationStatusChip';
import { getCountryLabel } from 'utils/countries';
import { GetOptionSet } from 'services/openapiClient';
import { IColumn } from 'kosmos/components/ResourceTable';
import { getKosmosReportLink } from 'utils/report';

const MINUTE = 60 * 1000;
const getLocalTime = (time: string) => new Date(+new Date(time) - new Date().getTimezoneOffset() * MINUTE);

const PREFIX = 'CompanyListDetailPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    }
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export interface ICountryOption {
  label: string;
  alpha3: string;
}

function CompanyListDetailPage(props: RouteComponentProps<{ id: string }>): JSX.Element {
  const id = props.match.params.id;
  const [openCreatedNotice, setOpenCreatedNotice] = React.useState(false);
  const [tabId, setTabId] = React.useState('details');
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [countries, setCountries] = React.useState<ICountryOption[]>([]);


  React.useEffect(() => {
    const fetchCountries = async () => {
      const options = await GetOptionSet('countries');
      const formattedOptions = options.map(item => ({
        label: item.label || '',
        alpha3: item.alias || ''
      }));
      setCountries(formattedOptions);
    };

    fetchCountries();
  }, []);


  const handleFetch = async (): Promise<CompanyListDto> => {
    const companyList = await GetCompanyList(id);
    return companyList;
  };

  const handleCloseCreatedNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenCreatedNotice(false);
  };

  const generateCompanyColumns = (): IColumn<CompanyListCompanyDto>[] => {
    return [
      {
        label: 'Input Name',
        sortIndex: 'name',
        render: (item) => item.name || ''
      },
      {
        label: 'Company Number',
        render: (item) => item.registrationNumbers || ''
      },
      {
        label: 'External Identifier',
        render: (item) => {
          if (item.company?.externalIdentifiers && item.company?.externalIdentifiers?.length) {
            return <div>{item.company?.externalIdentifiers.map(aReg => <>{aReg.typeDescription}: {aReg.identifier}<br/></> )}</div>;
          }
          return '';
        },
      },
      {
        label: 'Supplier Name',
        render: (item) => item.company?.name || ''
      },
      {
        label: 'Country',
        dataIndex: 'countryCode',
        render: (item) => getCountryLabel(item.countryCode),
      },
      {
        label: 'Status',
        render: (item) => (
          <VerificationStatusChip
            verificationStatus={item.verificationStatus}
            isDissolved={item.company?.statusInfo === 'Dissolved'}
            scoringState={item.company?.scoringState}
            reportSnapshot={item.kosmosReportSnapshotDto}
          />
        ),
      },
      {
        label: 'Score',
        render: (item) => {
          const completed = item.company?.scoringState === 'Completed';
          if (completed && item.kosmosReportSnapshotDto) {
            const { overallScore, companyId, organisationId } = item.kosmosReportSnapshotDto;
            return (
              <span>
                <a href={getKosmosReportLink(companyId, organisationId)} target="_blank" rel="noreferrer">
                  {overallScore}
                </a>
              </span>
            );
          } else {
            return 'N/A';
          }
        }
      },
    ];
  };
  const generateTabs = (): ITab<CompanyListDto>[] => [
    {
      id: 'details',
      title: 'Details',
      getUiSchema: () => ({
        fields: [
          {
            label: 'Id',
            name: 'id',
            readonly: true,
            widget: 'text',
          },
          {
            label: 'Name',
            name: 'name',
            widget: 'text'
          },
          {
            label: 'Description',
            name: 'description',
            widget: 'text'
          },
          {
            label: 'Last Score Triggered At',
            formatter: (datestr: string) => {
              return (datestr == '0001-01-01T00:00:00') ? 'Not Triggered Yet' : date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm');
            },
            name: 'lastTriggeredAt',
            readonly: true,
            widget: 'text'
          },
          {
            label: 'Created At',
            formatter: (datestr: string) => date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm'),
            name: 'created',
            readonly: true,
            widget: 'text'
          },
          {
            label: 'Last Updated At',
            name: 'updated',
            formatter: (datestr: string) => date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm'),
            readonly: true,
            widget: 'text'
          },
        ]
      }),
    },
    {
      id: 'companies',
      title: 'Suppliers',
      render: (companyList: CompanyListDto) => {
        return (
          <div>
            <CompanyListEditTable
              countries={countries}
              companyListId={companyList.id}
              onErrorResponse={handleError}
            />
          </div>
        );
      },
    },
    {
      id: 'verifiedCompanies',
      title: 'Scoring Results',
      render: (companyList: CompanyListDto) => {
        return (
          <div>
            <CompanyListVerificationTable
              countries={countries}
              onErrorResponse={handleError}
              columns={generateCompanyColumns()}
              companyListId={companyList.id}
            />
          </div>
        );
      },
    }
  ];

  const handleUpdate = async (attrs: { name?: string, display?: string }): Promise<CompanyListDto> => {
    const org = await UpdateCompanyList(id, attrs);
    setNonce(generateNonce());
    return org;
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      // console.log(e.response?.data);
      const errorMessage = get(e.response?.data, 'message')
        || get(e.response?.data, 'title')
        || 'Something went wrong';
      setOpenCreatedNotice(true);
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setOpenCreatedNotice(true);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  return (
    <Root>
      <ResourceDetailContainer
        key={nonce}
        tabs={generateTabs()}
        breadcrumbText="Company Lists"
        breadcrumbUrl="/companyLists"
        onTabChanged={setTabId}
        onFetch={handleFetch}
        onUpdate={handleUpdate}
        currentTabId={tabId}
      />
      {noticeData && (
        <Notice
          open={openCreatedNotice}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseCreatedNotice}
        />
      )}
    </Root>
  );
}

export default withRouter(CompanyListDetailPage);
